import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import {OLIGO_MAX_LENGTH, SEQUENCE_MAX_LENGTH} from 'constants/SequenceConstants';
import MeltingTempModal from 'molbio/melting-temp/MeltingTempModal';
import SequenceNUEModule from 'onboarding/SequenceNUEModule';
import Tooltip from 'ui-library/bs/Tooltip';
import {ORANGE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {Optional} from 'util/Types';

interface SequenceStatusBarProps {
  isOligo: boolean;
  nucleotideType: NucleotideType;
  numBases: number | string | null;
  selectionStart: Optional<number>;
  selectionEnd: Optional<number>;
  selectionLength: Optional<number>;
  selectionGC: Optional<string | number>;
  selectionMeltingTmp: Optional<string | number>;
}

const StatusBarItem = (props: {label: string; value: string | number | null}): JSX.Element => (
  <div className="statusbar-group" key={props.label}>
    <span className="statusbar-label">{props.label}</span>
    <span className="statusbar-field selectable">{props.value}</span>
  </div>
);

function MeltingTemperature({
  selectionMeltingTmp,
  isOligo,
  nucleotideType,
}: {
  selectionMeltingTmp: number | string | null;
  isOligo: boolean;
  nucleotideType: NucleotideType;
}): JSX.Element {
  return (
    <div className="statusbar-group" key="mt">
      <div
        className="btn btn-af-default"
        onClick={() => {
          legacyRunModal(MeltingTempModal, {isOligo, nucleotideType});
        }}
      >
        <span className="statusbar-label">Melting Temp</span>
        <span className="statusbar-field selectable">
          {(selectionMeltingTmp && `${selectionMeltingTmp} °C`) || '--'}
        </span>
      </div>
    </div>
  );
}

function SelectionStatus(props: Omit<SequenceStatusBarProps, 'numBases'>): JSX.Element | null {
  if (props.selectionEnd) {
    return (
      <>
        {/* @ts-expect-error: value is initially undefined, unclear if we can narrow the type */}
        <StatusBarItem label="Start" value={props.selectionStart} />
        <StatusBarItem label="End" value={props.selectionEnd} />
        {/* @ts-expect-error: value is initially undefined, unclear if we can narrow the type */}
        <StatusBarItem label="Length" value={props.selectionLength} />
        <StatusBarItem label="GC" value={(props.selectionGC && `${props.selectionGC}%`) || '--'} />
        <MeltingTemperature
          // @ts-expect-error: value is initially undefined, unclear if we can narrow the type
          selectionMeltingTmp={props.selectionMeltingTmp}
          isOligo={props.isOligo}
          nucleotideType={props.nucleotideType}
        />
      </>
    );
  }
  if (props.selectionStart != null) {
    return <StatusBarItem label="Insert" value={props.selectionStart} />;
  }
  return null;
}

export default function SequenceStatusBar(props: SequenceStatusBarProps): JSX.Element {
  const lengthWarning = props.isOligo
    ? "Oligo exceeds Benchling's limit of 2,000 bases. This may affect system performance."
    : "Sequence exceeds Benchling's limit of 10 million bases. This may affect system performance.";
  const limit = props.isOligo ? OLIGO_MAX_LENGTH : SEQUENCE_MAX_LENGTH;

  return (
    <div>
      <div className="statusbar-section always">
        <div className="statusbar-group">
          <div className="statusbar-label">Bases</div>
          <div className="statusbar-field selectable">{props.numBases ?? '--'}</div>
          {BenchlingConfig.MOLBIO_ENFORCE_SEQUENCE_LENGTH_LIMITS &&
            props.numBases != null &&
            // @ts-expect-error: value is initially '--' so we may be comparing number against a string
            props.numBases > limit && (
              <Tooltip title={lengthWarning}>
                <BenchlingIcon style={{marginLeft: '3px', color: ORANGE}} icon="warning" />
              </Tooltip>
            )}
        </div>
      </div>
      <div className="statusbar-section">
        <SelectionStatus
          selectionStart={props.selectionStart}
          selectionEnd={props.selectionEnd}
          selectionLength={props.selectionLength}
          selectionGC={props.selectionGC}
          selectionMeltingTmp={props.selectionMeltingTmp}
          isOligo={props.isOligo}
          nucleotideType={props.nucleotideType}
        />
      </div>
      <div className={css(styles.sequenceNUEModule)}>
        <SequenceNUEModule />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  sequenceNUEModule: {
    position: 'absolute',
    bottom: '32px',
    lineHeight: 'initial',
  },
});
